<template>
    <q-card id="seafloorFeaturePane" v-bind="attrs" v-if="seafloorBarData">
        <div>
            <div class="row q-pl-sm text-h6 text-weight-medium">Depth<q-btn dense size="xs" flat class="gray" icon="help_outline" @click="dataConsiderations['depth'].model = !dataConsiderations['depth'].model" aria-label="Depth data help"/></div>
            <q-separator class="q-ml-sm q-mr-lg" color="primary"/>
            <div v-if="minMeanData" class="row items-center q-pa-sm">
                <div class="col-5 q-pr-md">
                    <NumberLine name="Depth" divName="depthDiv" :min="minMeanData['DEPTH'].min" :max="minMeanData['DEPTH'].max" :value="minMeanData['DEPTH'].median" unit="m"/>
                </div>
                <div class="col-6 q-pr-md q-pt-sm text-caption">
                    Meters from water surface. 
                </div>
                <div class="col-1 items-center self-center bordered pointer" :class="mapStyleInfo['attr'] == 'DEPTH' ? 'mapActive' : ''" @click="mapStyleClick('DEPTH', 'step')">
                    <q-icon class="self-center q-pb-md" v-if="mapStyleInfo['attr'] != 'DEPTH'" name="play_arrow" ></q-icon>
                    <q-icon  class="self-center q-pb-md" v-else name="star" ></q-icon>
                </div>
            </div>
        </div>
        <div>
            <div>
                <div class="row q-pl-sm q-mt-md text-h6 text-weight-medium">Slope <q-btn dense size="xs" flat class="gray" icon="help_outline" @click="dataConsiderations['slope'].model = !dataConsiderations['slope'].model" aria-label="Slope data help"/></div>
                <q-separator class="q-ml-sm q-mr-lg" color="primary"/>
                <div row class="q-pa-sm">Slope can be used as an indicator for hardbottom.  Areas >30% are considered potential hardbottom. 
                    <span >The most common class in this area is <span class="text-bold">{{getBarCategoryWithLargestValue(seafloorBarData['SLOPE'])}}</span></span>
                </div>
                <div v-if="seafloorBarData" class="row items-center q-pa-sm">

                    <div class="col-11">
                        <StackedBar 
                            :data="seafloorBarData['SLOPE']" 
                            divName="slope" 
                            categoryProp="category" 
                            barName="slope" 
                            valueProp="value" 
                            class="q-mt-none" 
                            :xAxisTicks="false"
                            :categoryColors="slopeColors"
                            :topMargin="0"    
                            :leftMargin="0"  
                            :rectOutline="true"                                 
                        />
                    </div>
                    <div class="col-1 items-center self-center bordered pointer q-mb-sm" :class="mapStyleInfo['attr'] == 'SLOPE' ? 'mapActive' : ''" @click="mapStyleClick('SLOPE')">
                        <q-icon class="self-center q-pb-md" size="xs" v-if="mapStyleInfo['attr'] != 'SLOPE'" name="play_arrow"></q-icon>
                        <q-icon  class="self-center q-pb-md" v-else name="star" ></q-icon>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <div class="row q-pl-sm  q-mt-md text-h6 text-weight-medium">Seabed Forms<q-btn dense size="xs" flat class="gray" icon="help_outline" @click="dataConsiderations['depth'].model = !dataConsiderations['depth'].model" aria-label="Seabed forms data help"/> </div>
            <q-separator class="q-ml-sm q-mr-lg" color="primary"/>
            <div class="row q-pa-sm">
                <div class="col-12">
                    Seabed forms describe the structure of the seafloor. The most common class in this area is 
                    <span class="text-bold">{{ getBarCategoryWithLargestValue(seafloorBarData['SEABED']) }}</span>
                </div>                
            </div>
            <div v-if="seafloorBarData" class="row items-center q-pa-sm">
                <div class="col-11">
                    <StackedBar 
                        :data="seafloorBarData['SEABED']" 
                        divName="seabedforms" 
                        categoryProp="category" 
                        barName="seabedforms" 
                        valueProp="value" 
                        class="q-mt-none" 
                        :xAxisTicks="false"
                        :categoryColors="seabedformColors"
                        :topMargin="0"    
                        :leftMargin="0"  
                        :rectOutline="true"                                 
                    />
                </div>
                <div class="col-1 items-center self-center bordered pointer q-mb-sm" :class="mapStyleInfo['attr'] == 'SEABED' ? 'mapActive' : ''" @click="mapStyleClick('SEABED')">
                    <q-icon class="self-center q-pb-md" size="xs" v-if="mapStyleInfo['attr'] != 'SEABED'" name="play_arrow"></q-icon>
                    <q-icon  class="self-center q-pb-md" v-else name="star" ></q-icon>
                </div>
            </div>
        </div>
        <div>
            <div class="row q-pl-sm  q-mt-md text-h6 text-weight-medium">Sediment<q-btn dense size="xs" flat class="gray" icon="help_outline" @click="dataConsiderations['sediment'].model = !dataConsiderations['sediment'].model" aria-label="Sediment data help"/> </div>
            <q-separator class="q-ml-sm q-mr-lg" color="primary"/>
            <div class="row q-pa-sm">
                <div class="col-12">
                    The most common sediment class in this area is 
                    <span class="text-bold">{{ getBarCategoryWithLargestValue(seafloorBarData['SEDIM']) }}</span>
                </div>
            </div>
            <div v-if="seafloorBarData" class="row items-center q-pa-sm">
                <div class="col-11">
                    <StackedBar 
                        :data="seafloorBarData['SEDIM']" 
                        divName="sediment" 
                        categoryProp="category" 
                        barName="sediment" 
                        valueProp="value" 
                        class="q-mt-none" 
                        :xAxisTicks="false"
                        :categoryColors="sedimentColors"
                        :topMargin="0"    
                        :leftMargin="0"  
                        :rectOutline="true"   
                        :sortCategories="sedimentBarSortOrder"                              
                    />
                </div>
                <div class="col-1 items-center self-center bordered pointer q-mb-sm" :class="mapStyleInfo['attr'] == 'SEDIM' ? 'mapActive' : ''" @click="mapStyleClick('SEDIM', 'categorical')">
                    <q-icon class="self-center q-pb-md" size="xs" v-if="mapStyleInfo['attr'] != 'SEDIM'" name="play_arrow"></q-icon>
                    <q-icon  class="self-center q-pb-md" v-else name="star" ></q-icon>
                </div>

            </div>
        </div>
        <div>
            <div class="row q-pl-sm  q-mt-md text-h6 text-weight-medium">Hardbottom & Coral Habitat<q-btn dense size="xs" flat class="gray" icon="help_outline" @click="dataConsiderations['hardbottom'].model = !dataConsiderations['hardbottom'].model" aria-label="Hardbottom data help"/></div>
            <q-separator class="q-ml-sm q-mr-lg" color="primary"/>
            <div v-if="hardbottom" class="row q-pa-sm">
                <div class="col-11 text-caption">
                    <div class="row items-center text-body2 q-pb-sm">
                        <q-icon v-if="hardbottom.LikelyHardbottom" class="q-pr-xs" size="sm" name="check_circle" color="secondary"/>
                        <q-icon v-else name="cancel"  class="q-pr-xs" size="sm"/> 
                        Observed Hardbottom
                    </div>
                    <div class="row items-center text-body2 q-pb-sm">
                        <q-icon v-if="hardbottom.LikelyCoral" class="q-pr-xs" size="sm" name="check_circle" color="secondary"/>
                        <q-icon v-else name="cancel"  class="q-pr-xs" size="sm"/> 
                        Observed Corals
                    </div>
                    <div class="row q-pl-xs"><span class="text-bold">{{ hardbottom.DirectObs }}</span>&nbsp; Cells with direct observation of hardbottom</div>
                    <div class="row q-pl-xs"><span class="text-bold">{{ hardbottom.CoralsSponges }}</span>&nbsp;  Cells with observations of corals/sponges</div>
                    <div class="row q-pl-xs"><span class="text-bold">{{ hardbottom.SlopeGT30 }}</span>&nbsp; Cells w/ slope >30%, signaling potential hardbottom</div>
                </div>

                <div class="col-1 items-center self-center bordered pointer" :class="mapStyleInfo['attr'] == 'HARDB' ? 'mapActive' : ''" @click="mapStyleClick('HARDB', 'categorical')">
                    <q-icon class="self-center q-pb-md" size="xs" v-if="mapStyleInfo['attr'] != 'HARDB'" name="play_arrow"></q-icon>
                    <q-icon  class="self-center q-pb-md" v-else name="star" ></q-icon>
                </div>

            </div>

        </div>
    </q-card>
    <div v-for="(infoObj, name) in infoDialogs" :key="name" >
        <q-dialog v-model="infoObj.model">
            <InfoDialog  :html="infoObj.text" :name="infoObj.pName"/>
        </q-dialog>
    </div>
    <div v-for="(infoObj, name) in dataConsiderations" :key="name" >
        <q-dialog v-model="infoObj.model">
            <q-card>
            <q-card-section>
                <FeaturesDataConsiderations :groupsToShow="name" />
            </q-card-section>
            <q-card-actions align="right">
                <q-btn flat label="OK" color="primary" @click="infoObj.model = false" />
            </q-card-actions>
            </q-card>
        </q-dialog>
    </div>

</template>
<script setup lang="ts">
import { toRefs, ref, onMounted, onUnmounted, computed, useAttrs  } from "vue";
import { useHelpers } from "../composables/useHelpers"
import { useMapItems } from "../composables/useMapItems"
import { stops } from "../assets/ColorStops.json"
import NumberLine from "./NumberLine.vue";
import StackedBar from "./StackedBar.vue";
import InfoDialog from "./InfoDialog.vue";
import FeaturesDataConsiderations from "./FeaturesDataConsiderations.vue";
const {  ecos, styleMap, setLegend, mapStyleInfo, clearMapStyle, activeLayerID, setDataQuality, loadDataByID, setProcessedData, processedData } = useMapItems()
const {  getMedian, getMean, countOccurrences, getBarCategoryWithLargestValue } = useHelpers();
const attrs = useAttrs();

const props = defineProps({
    data: { type: Object },
    version: {type: Number},

})
const {  data, version } = toRefs(props)

const seafloorData =  computed(() =>{
    // console.log(data.value )
    if (data.value && data.value["SEAFLOOR_FEATURES"] && data.value["SEAFLOOR_FEATURES"].length >0){
        return data.value["SEAFLOOR_FEATURES"]
    }
    else{ return null}
})

const layerPrettyNames = {
    "SEABED" : "Seabed Forms",
    "Depth" : "Depth",
    "SEDIM" : "Sediment",
    "HARDB" : "Hard bottom",
    "SLOPE" : "Slope"
}

const minMeanData = computed(() =>{
    if (
        !processedData.value[version.value] || 
        processedData.value[version.value]['SEAFLOOR_FEATURES'] === undefined || 
        processedData.value[version.value]['SEAFLOOR_FEATURES']['minMeanData'] ===undefined
    ){
        const minMeanAttrs = ["DEPTH"]
        const resultVals = {}
        const results = {}

        minMeanAttrs.forEach((attr) =>{
            resultVals[attr] = []
            results[attr] = {}
        })

        if (seafloorData.value){
            seafloorData.value.forEach((cell) =>{
                minMeanAttrs.forEach((attr) =>{
                    resultVals[attr].push(cell[attr])
                })
            })
            minMeanAttrs.forEach((attr) =>{
                results[attr]['min'] = Math.round(Math.min(...resultVals[attr]))
                results[attr]['max'] = Math.round(Math.max(...resultVals[attr]))
                results[attr]['median'] = Math.round(getMedian(resultVals[attr]))
                results[attr]['mean'] = Math.round(getMean(resultVals[attr]))
            })
            // console.log(resultVals)
            setProcessedData(version.value, "SEAFLOOR_FEATURES", 'minMeanData', null, results)
            return results
        }
        else{
            return null
        }   
    }
    else{
        return processedData.value[version.value]['SEAFLOOR_FEATURES']['minMeanData']
    }
    
})


const seafloorBarData = computed(() =>{
    if (
        !processedData.value[version.value] || 
        processedData.value[version.value]['SEAFLOOR_FEATURES'] === undefined || 
        processedData.value[version.value]['SEAFLOOR_FEATURES']['barData'] ===undefined
    ){
        const attrs = ["SEABED", "SLOPE", "SEDIM"]
        const resultVals = {}
        const results = {}

        attrs.forEach((attr) =>{
            resultVals[attr] = []
            results[attr] = []
        })
        // console.log(results)
        if (seafloorData.value){
            seafloorData.value.forEach((cell) =>{
                attrs.forEach((attr) =>{
                    resultVals[attr].push(cell[attr])
                })
            })

            // get the # of cells -- used to calc % 
            const numCells = (resultVals['SEABED'].length); // same for all

            attrs.forEach((attr) =>{
                // loop through the categories and count the occurances of each
                for (const [code, name] of Object.entries(stopNames[attr])) {
                    results[attr].push({
                        category : name,
                        value: Math.round((countOccurrences(resultVals[attr], parseInt(code))/ numCells *100) *10 /10)
                    })
                        
                }
            })
            // console.log(results)
            setProcessedData(version.value, "SEAFLOOR_FEATURES", 'barData', null, results)
            return results

        }
        else{
            return null
        }
    }
    else{
        return processedData.value[version.value]['SEAFLOOR_FEATURES']['barData']
    }
})

const hardbottom = computed(() =>{
    if (
        !processedData.value[version.value] || 
        processedData.value[version.value]['SEAFLOOR_FEATURES'] === undefined || 
        processedData.value[version.value]['SEAFLOOR_FEATURES']['hardbottom'] ===undefined
    ){
        if (seafloorData.value){
            const resultVals = []
            let likelyHB = false
            let likelyCoral = false
            seafloorData.value.forEach((cell) =>{
                resultVals.push(cell['HARDB'])
                if(cell['HARDB'] == 1 || cell['HARDB'] == 4){likelyHB = true}
                if(cell['HARDB'] == 2 || cell['HARDB'] == 4){likelyCoral = true}
            })
            const results = {
                'LikelyHardbottom' : likelyHB,
                "LikelyCoral" : likelyCoral,
                'DirectObs' : countOccurrences(resultVals, 1) + countOccurrences(resultVals, 4),
                'CoralsSponges' : countOccurrences(resultVals, 2) + countOccurrences(resultVals, 4),
                'SlopeGT30' : countOccurrences(resultVals, 3)
            }
            setProcessedData(version.value, "SEAFLOOR_FEATURES", 'hardbottom', null, results)
            return results
        }
        else{
            return null
        }
    }
    else{
        return processedData.value[version.value]['SEAFLOOR_FEATURES']['hardbottom']
    }
    
})

const mapStyleClick = (attr, type='interpolate') =>{
    let lyrID
    if (activeLayerID.value.includes("QUALITY")){
        lyrID = activeLayerID.value.replace("_QUALITY", "")
        setDataQuality(false)
        loadDataByID(lyrID, version.value)
    }
    else{lyrID = activeLayerID.value}

    // don't pass a valueNames object into style map for depth-- display the raw values
    let valueNames
    if (attr == "DEPTH"){valueNames = null}
    else{valueNames =stopNames[attr] }
    styleMap(lyrID, attr, type, stops[attr], valueNames), 
    setLegend(layerPrettyNames[attr] + ' ', stops[attr], stopNames[attr])
}

const stopNames = {
    "DEPTH" : {
        "-9": "No Data",
        "0": "<10",
        "10": "10 - 25m",
        "25": "25 - 50m",
        "50": "50 - 75m",
        "75": "75 - 100m",
        "100": "100 - 150m",
        "150": "150 - 200m",
        "200": "200 - 300m",
        "300": "300 - 500m",
        "500": "500 - 2750m",
        "2750": ">2750m"
    },
    "SLOPE" :{
        "-9" : "No Data",
        0 : "<10%",
        10: "10-20%",
        20: "20-30%",
        30: ">30%"
    },
    "SEABED" :{
        "-9" : "No Data",
        "10" : "Depression/valley",
        "15" : "Valley peaks",
        "20" : "Low flat",
        "30" : "Mid flat",
        "40" : "Upper flat/bank",
        "45" : "Upper slope/peak"
    },
    "SEDIM" : {
        "1000" : "Majority sand",
        "2000" : "Majority mud",
        "5000" : "Majority gravel",
        "120" : "Sand with some mud",
        "150" : "Sand with some gravel",
        "170" : "Sand with some mud and gravel",
        "210" : "Mud with some sand",
        "250" : "Mud with some gravel",
        "260" : "Mud with some sand and gravel",
        "510" : "Gravel with some sand",
        "520" : "Gravel with some mud",
        "530" : "Gravel with some sand and mud",
        "30" : "Mix of sand and mud",
        "60" : "Mix of sand and gravel",
        "70" : "Mix of mud and gravel",
        "80" : "Mix of sand, mud and gravel",
        "-9" : "No Data"
    },

    
    "HARDB" : {
        "-9" : "No Data",
        "0" : "Multibeam data, but slope <30%",
        "1" : "Observed Hardbottom",
        "2" : "Observed corals/sponges",
        "3" : "Slope >30%, signaling potential hardbottom",
        "4" : "Observed Hardbottom & Corals"
    }
}


const seabedBarSortOrder = ["Depression/valley", "Valley peaks", "Low flat", "Mid flat",  "Upper flat/bank", "Upper slope/peak"]
const seabedformColors = {
    "No Data" : "#aeaeae",
    "Depression/valley" : "#7A8EF5",
    "Valley peaks" : "#444F89",
    "Low flat" : "#FAFAFA",
    "Mid flat" : "#FFF1BE",
    "Upper flat/bank" : "#FFBEE8",
    "Upper slope/peak" : "#CD6699"
}

const slopeColors = {
    "No Data": "#aeaeae",
    "<10%": "#F0E9FF",
    "10-20%": "#DACAFD",
    "20-30%": "#7C66AD",
    ">30%": "#413360"
}

const sedimentBarSortOrder = ["Majority gravel", "Gravel with some sand", "Gravel with some mud", "Gravel with some sand and mud", "Majority sand", "Sand with some mud", "Sand with some gravel", "Sand with some mud and gravel", "Majority mud", "Mud with some sand", "Mud with some gravel", "Mud with some sand and gravel", "Mix of sand and mud", "Mix of sand and gravel", "Mix of mud and gravel", "Mix of sand, mud and gravel", "No Data"  ]
const sedimentColors = {
    "Majority sand": "#BA7C5D",
    "Majority mud": "#448970",
    "Majority gravel": "#72468B",
    "Sand with some mud": "#FFFFBE",
    "Sand with some gravel": "#FFD37F",
    "Sand with some mud and gravel": "#FDAE88",
    "Mud with some sand": "#BEFFE8",
    "Mud with some gravel": "#7AF5CA",
    "Mud with some sand and gravel": "#66CDAB",
    "Gravel with some sand": "#E8BEFF",
    "Gravel with some mud": "#DF73FF",
    "Gravel with some sand and mud": "#C500FF",
    "Mix of sand and mud": "#E1E1E1",
    "Mix of sand and gravel": "#B2B2B2",
    "Mix of mud and gravel": "#828282",
    "Mix of sand, mud and gravel": "#4E4E4E",
    "No Data": "#f5f5f5"
};

const ecoregionMeanMaxDepths = {
    1 :{
        "mean" : "X",
        "max" : "X"
    },
    2 :{
        "mean" : "X",
        "max" : "X"
    },
    3 :{
        "mean" : "X",
        "max" : "X"
    },
}

const infoDialogs = ref({
    depth: {
        pName: "Depth",
        model: false,
        text: ""
    }
})

const dataConsiderations = ref({
    depth : {
        model:false
    },
    sediment: {
        model:false
    },
    hardbottom :{
        model:false
    },
    slope:{
        model: false
    }
})

onUnmounted(() =>{
    clearMapStyle()
})

onMounted(() => {
    // if coming from a route (entered via URL).  Load the data
    if (activeLayerID.value != 'SEAFLOOR_FEATURES'){
        loadDataByID('SEAFLOOR_FEATURES', version)
    }
})

</script>
<style scoped>
.mapActive{
    background-color: #1D8177;
}
.bordered{
    border: 1px solid black;
    text-align: center;
    max-height: 18px;
    vertical-align: middle;
    border-radius: 3px;
}
.pointer{
    cursor: pointer;
}
</style>